import moment, {type Moment} from 'moment-timezone';

import {isNonEmpty} from 'web-app/util/non-empty-array';

import * as DateFormatter from '../helpers/date-formatter';
import type * as Types from '../types';

const NUMBER_OF_PERIODS_DISPLAYED = 5;
const NUMBER_OF_WEEKLY_PERIODS_DISPLAYED = 17;

const numberOfPeriods = (billingType: 'weekly' | 'monthly' | 'biweekly') => {
    if (billingType === 'weekly') {
        return [...Array(NUMBER_OF_WEEKLY_PERIODS_DISPLAYED).keys()];
    }
    return [...Array(NUMBER_OF_PERIODS_DISPLAYED).keys()];
};

const customPeriod: Types.CustomPeriod = {
    startDate: undefined,
    endDate: undefined,
    unformattedStartDate: undefined,
    type: 'custom',
};

const monthlyPeriods = (startDate: Moment): null | Types.MonthlyPeriods => {
    const periods = [
        ...numberOfPeriods('monthly').map<Types.Month>(numberOfPeriod => {
            const month = startDate.clone().add(numberOfPeriod, 'month').format('MMMM');
            const unformattedStartDate = startDate.clone().add(numberOfPeriod, 'month').startOf('month');
            const unformattedEndDate = startDate.clone().add(numberOfPeriod, 'month').endOf('month');
            const year = startDate.clone().add(numberOfPeriod, 'month').startOf('month').format('YYYY');
            return {
                month,
                startDate: {
                    numericFormat: DateFormatter.numericFormat(unformattedStartDate),
                    textFormat: DateFormatter.textFormat(unformattedStartDate),
                },
                endDate: {
                    numericFormat: DateFormatter.numericFormat(unformattedEndDate),
                    textFormat: DateFormatter.textFormat(unformattedEndDate),
                },
                year,
                unformattedStartDate,
                unformattedEndDate,
                type: 'generic',
            };
        }),
        customPeriod,
    ];
    if (!isNonEmpty(periods)) {
        return null;
    }
    return {type: 'monthly', periods};
};

const weeklyPeriods = (startDate: Moment): null | Types.WeeklyPeriods => {
    const periods = [
        ...numberOfPeriods('weekly').map<Types.Week>(numberOfPeriod => {
            const unformattedStartDate = startDate.clone().startOf('isoWeek').add(numberOfPeriod, 'week');
            const unformattedEndDate = startDate
                .clone()
                .startOf('isoWeek')
                .add(numberOfPeriod, 'week')
                .endOf('isoWeek');
            const year = startDate.clone().startOf('isoWeek').add(numberOfPeriod, 'week').format('YYYY');
            return {
                startDate: {
                    numericFormat: DateFormatter.numericFormat(unformattedStartDate),
                    textFormat: DateFormatter.textFormat(unformattedStartDate),
                },
                endDate: {
                    numericFormat: DateFormatter.numericFormat(unformattedEndDate),
                    textFormat: DateFormatter.textFormat(unformattedEndDate),
                },
                year,
                unformattedStartDate,
                unformattedEndDate,
                type: 'generic',
            };
        }),
        customPeriod,
    ];
    if (!isNonEmpty(periods)) {
        return null;
    }
    return {type: 'weekly', periods};
};

const biweeklyPeriods = (startDate: Moment): null | Types.BiweeklyPeriods => {
    const periods = [
        ...numberOfPeriods('biweekly').map<Types.TwoWeeks>((numberOfPeriod, index) => {
            const unformattedStartDate = startDate.clone().add(numberOfPeriod * 2, 'week');
            const unformattedEndDate = unformattedStartDate.clone().add(1, 'week').endOf('isoWeek');

            const periodStartDate = index === 0 ? startDate : unformattedStartDate;

            return {
                startDate: {
                    numericFormat: DateFormatter.numericFormat(periodStartDate),
                    textFormat: DateFormatter.textFormat(periodStartDate),
                },
                endDate: {
                    numericFormat: DateFormatter.numericFormat(unformattedEndDate),
                    textFormat: DateFormatter.textFormat(unformattedEndDate),
                },
                year: unformattedEndDate.format('YYYY'),
                unformattedStartDate,
                unformattedEndDate,
                type: 'generic',
            };
        }),
        customPeriod,
    ];
    if (!isNonEmpty(periods)) {
        return null;
    }
    return {type: 'biweekly', periods};
};

const biweeklyBillingStartDate = ({
    billingFrequencies,
    defaultStartDate,
}: {
    billingFrequencies: Array<Types.BillingFrequency>;
    defaultStartDate?: Moment;
}): Moment => {
    const existingStartDate = billingFrequencies.find(
        billingFrequency => billingFrequency.type === 'biweekly',
    )?.startDate;
    const existingBillingPeriod = billingFrequencies.find(
        billingFrequency => billingFrequency.type !== 'none',
    )?.autoBillingPeriod;

    const _defaultStartDate = defaultStartDate ?? moment();
    const defaultBillingPeriod = 'CURRENT';

    const closestStartDate = getClosestBiweeklyStartDate(moment(existingStartDate), _defaultStartDate);

    const startDate = existingStartDate ? closestStartDate : _defaultStartDate;
    const invoicingPeriod = existingBillingPeriod || defaultBillingPeriod;

    return calculateFirstBillingPeriodStartDate(startDate, invoicingPeriod);
};

const calculateFirstBillingPeriodStartDate = (startDate: Moment, invoicingPeriod: Types.AutoBillingPeriod): Moment => {
    const firstBillingPeriodStartDate = (() => {
        switch (invoicingPeriod) {
            case 'NEXT': {
                return startDate.clone().startOf('isoWeek').add(1, 'week');
            }
            case 'CURRENT': {
                return startDate.clone().startOf('isoWeek');
            }
            case 'PREVIOUS': {
                return startDate.clone().startOf('isoWeek').subtract(2, 'weeks');
            }
            default: {
                return startDate;
            }
        }
    })();

    return firstBillingPeriodStartDate;
};

const calculateBiweeklyCustomPeriodEndDate = (biweeklyBillingStartDate: Moment, customStartDate: Moment) => {
    const closestBiweeklyStartDate = getClosestBiweeklyStartDate(biweeklyBillingStartDate, customStartDate);

    const isInSameWeek = customStartDate.isSame(closestBiweeklyStartDate, 'isoWeek');

    return isInSameWeek
        ? customStartDate.clone().add(1, 'week').endOf('isoWeek')
        : customStartDate.clone().endOf('isoWeek');
};

const calculateStartDateOfBiweeklyEndPeriod = (biweeklyBillingStartDate: Moment, selectedEndDate: Moment) => {
    const closestBiweeklyStartDate = getClosestBiweeklyStartDate(biweeklyBillingStartDate, selectedEndDate);

    const isInSameWeek = selectedEndDate.isSame(closestBiweeklyStartDate, 'isoWeek');

    return isInSameWeek ? closestBiweeklyStartDate : selectedEndDate.clone().subtract(1, 'week').startOf('isoWeek');
};

const getClosestBiweeklyStartDate = (biweeklyBillingStartDate: Moment, date: Moment) => {
    const daysDifference = date.diff(biweeklyBillingStartDate, 'days');
    const biweeklyInterval = Math.round(daysDifference / 14);
    return biweeklyBillingStartDate.clone().add(biweeklyInterval * 14, 'days');
};

export {
    monthlyPeriods,
    weeklyPeriods,
    biweeklyPeriods,
    calculateFirstBillingPeriodStartDate,
    biweeklyBillingStartDate,
    calculateBiweeklyCustomPeriodEndDate,
    calculateStartDateOfBiweeklyEndPeriod,
    getClosestBiweeklyStartDate,
};
